import React from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CalendarDays, TriangleAlert } from 'lucide-react';
import { EmptyState, ErrorState, KpiGrid, KpiTile, PersonCell, SectionCard, StatusPill, TableSkeletonRows } from '../../components/ui';
import { TimeOffStatus } from '../../store/slices/timeOffSlice';
import type { TimeOffEntry } from '../../store/slices/timeOffSlice';
import type { Employee } from '../../store/slices/employeeSlice';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDate, formatDateRange, formatDays, formatPercent } from '../../utils/format';
import { NO_DEPARTMENT_LABEL, TypeTag, entryDays, formatHours } from './shared';

export interface TeamCoverageRow {
  teamName: string;
  total: number;
  outToday: number;
  outNames: string[];
  coverage: number;
}

export interface PtoPacingRow {
  employee: Employee;
  expectedUsed: number;
  overBy: number;
  overPercent: number;
  used: number;
}

interface TeamOverviewPanelProps {
  activeToday: TimeOffEntry[];
  teamCoverage: TeamCoverageRow[];
  complianceIssues: TimeOffEntry[];
  upcomingLongPto: TimeOffEntry[];
  ptoPacingOver: PtoPacingRow[];
  today: Date;
  /** "Jan 1, 2026": the start of the loaded period. */
  periodStart: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpenCalendar: () => void;
}

const VISIBLE_ROWS = 8;
const COVERAGE_TARGET = 80;

const teamOf = (person: { teamName?: string | null; department?: { name?: string } | null }) =>
  person.teamName || person.department?.name || NO_DEPARTMENT_LABEL;

/** A compact table inside a card, with its own loading and empty states. */
const OverviewTable: React.FC<{
  id?: string;
  label: string;
  columns: { label: string; align?: 'left' | 'right' }[];
  loading: boolean;
  hasRows: boolean;
  empty: { title: string; body?: string };
  children: React.ReactNode;
}> = ({ id, label, columns, loading, hasRows, empty, children }) => {
  if (!loading && !hasRows) return <EmptyState compact title={empty.title} body={empty.body} />;
  return (
    <Box id={id} sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.label} align={column.align ?? 'left'}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{loading ? <TableSkeletonRows columns={columns.length} rows={4} /> : children}</TableBody>
      </Table>
    </Box>
  );
};

type CardKey = 'coverage' | 'out' | 'upcoming' | 'pacing';

const FooterNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
    {children}
  </Typography>
);

/** "Showing 8 of 23 teams" + "Show all 23" / "Show fewer", expanding the table in place. */
const ShowAllFooter: React.FC<{
  total: number;
  noun: string;
  plural?: string;
  expanded: boolean;
  onToggle: () => void;
  controls: string;
}> = ({ total, noun, plural, expanded, onToggle, controls }) => {
  if (total <= VISIBLE_ROWS) return null;
  return (
    <>
      <FooterNote>
        {expanded ? `Showing all ${formatCount(total, noun, plural)}` : `Showing ${VISIBLE_ROWS} of ${formatCount(total, noun, plural)}`}
      </FooterNote>
      <Button variant="text" size="small" onClick={onToggle} aria-expanded={expanded} aria-controls={controls}>
        {expanded ? 'Show fewer' : `Show all ${total}`}
      </Button>
    </>
  );
};

/** Pending entries count as out, so say so: approved and unapproved absences must read differently. */
const PendingPill: React.FC<{ entry: TimeOffEntry }> = ({ entry }) =>
  entry.status === TimeOffStatus.PENDING ? <StatusPill tone="warning">Pending approval</StatusPill> : null;

const TeamOverviewPanel: React.FC<TeamOverviewPanelProps> = ({
  activeToday,
  teamCoverage,
  complianceIssues,
  upcomingLongPto,
  ptoPacingOver,
  today,
  periodStart,
  loading,
  error,
  onRetry,
  onOpenCalendar,
}) => {
  const [expanded, setExpanded] = React.useState<Record<CardKey, boolean>>({ coverage: false, out: false, upcoming: false, pacing: false });
  const toggle = (key: CardKey) => setExpanded((current) => ({ ...current, [key]: !current[key] }));
  const limit = (key: CardKey) => (expanded[key] ? undefined : VISIBLE_ROWS);

  if (error) {
    return (
      <SectionCard>
        <ErrorState
          title="Couldn’t load the team overview"
          body="Employee or time-off data didn’t load. Try again in a minute."
          onRetry={onRetry}
        />
      </SectionCard>
    );
  }

  const lowCoverageTeams = teamCoverage.filter((team) => team.coverage < COVERAGE_TARGET && team.total > 0).length;
  const coverageRows = [...teamCoverage].sort((a, b) => a.coverage - b.coverage).slice(0, limit('coverage'));
  const outRows = activeToday.slice(0, limit('out'));
  const upcomingRows = upcomingLongPto.slice(0, limit('upcoming'));
  const pacingRows = ptoPacingOver.slice(0, limit('pacing'));
  const todayLabel = formatDate(today, { weekday: true });

  return (
    <Box>
      <KpiGrid>
        <KpiTile label="Out today" value={formatCount(activeToday.length, 'person', 'people')} caption={todayLabel} loading={loading} />
        <KpiTile
          label="Teams below 80%"
          value={formatCount(lowCoverageTeams, 'team')}
          caption="Coverage today"
          info="Coverage is the share of a team’s active employees who aren’t out today."
          loading={loading}
        />
        <KpiTile
          label="Compliance flags"
          value={formatCount(complianceIssues.length, 'request')}
          caption={`Since ${periodStart}`}
          info="PTO submitted with less than 2 weeks’ notice, or WFH requested by someone not on a hybrid schedule (needs VP approval)."
          loading={loading}
        />
        <KpiTile
          label="Long PTO ahead"
          value={formatCount(upcomingLongPto.length, 'request')}
          caption="3+ days, next 30 days"
          loading={loading}
        />
      </KpiGrid>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
        <SectionCard
          id="team-coverage"
          flush
          title="Coverage by team"
          description={`${todayLabel} · lowest coverage first`}
          footer={
            teamCoverage.length > VISIBLE_ROWS ? (
              <ShowAllFooter total={teamCoverage.length} noun="team" expanded={expanded.coverage} onToggle={() => toggle('coverage')} controls="team-coverage-table" />
            ) : undefined
          }
        >
          <OverviewTable
            id="team-coverage-table"
            label="Coverage by team today"
            loading={loading}
            hasRows={coverageRows.length > 0}
            empty={{ title: 'No team data yet', body: 'Teams appear here once employees are assigned to them.' }}
            columns={[{ label: 'Team' }, { label: 'Out', align: 'right' }, { label: 'Headcount', align: 'right' }, { label: 'Coverage', align: 'right' }]}
          >
            {coverageRows.map((team) => (
              <TableRow key={team.teamName}>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
                    {team.teamName}
                  </Typography>
                  {team.outNames.length > 0 && (
                    <Typography variant="body2" component="div" noWrap title={team.outNames.join(', ')} sx={{ color: sbg.text.secondary }}>
                      Out: {team.outNames.join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">{team.outToday}</TableCell>
                <TableCell align="right">{team.total}</TableCell>
                <TableCell align="right">
                  {team.coverage < COVERAGE_TARGET ? (
                    <StatusPill tone="warning" icon={<TriangleAlert aria-hidden="true" />} title="Below 80% coverage">
                      {formatPercent(team.coverage)}
                      <span className="sbg-sr-only">, below 80% coverage</span>
                    </StatusPill>
                  ) : (
                    formatPercent(team.coverage)
                  )}
                </TableCell>
              </TableRow>
            ))}
          </OverviewTable>
        </SectionCard>

        <SectionCard
          id="out-today"
          flush
          title="Out today"
          description="PTO, sick time and WFH active today"
          footer={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, width: '100%' }}>
              <ShowAllFooter
                total={activeToday.length}
                noun="person"
                plural="people"
                expanded={expanded.out}
                onToggle={() => toggle('out')}
                controls="out-today-table"
              />
              <Button variant="text" size="small" startIcon={<CalendarDays />} onClick={onOpenCalendar} sx={{ ml: 'auto' }}>
                Open calendar
              </Button>
            </Box>
          }
        >
          <OverviewTable
            id="out-today-table"
            label="Employees out today"
            loading={loading}
            hasRows={outRows.length > 0}
            empty={{ title: 'No one is out today', body: 'Approved and pending time off that covers today appears here.' }}
            columns={[{ label: 'Employee' }, { label: 'Type' }, { label: 'Dates' }]}
          >
            {outRows.map((entry) => (
              <TableRow key={entry._id}>
                <TableCell sx={{ minWidth: 200 }}>
                  <PersonCell size={24} name={entry.employee.name} secondary={teamOf(entry.employee)} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    <TypeTag type={entry.type} />
                    <PendingPill entry={entry} />
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateRange(entry.startDate, entry.endDate)}</TableCell>
              </TableRow>
            ))}
          </OverviewTable>
        </SectionCard>

        <SectionCard
          id="long-pto"
          flush
          title="Long PTO coming up"
          description="3 days or more, starting in the next 30 days"
          footer={
            upcomingLongPto.length > VISIBLE_ROWS ? (
              <ShowAllFooter total={upcomingLongPto.length} noun="request" expanded={expanded.upcoming} onToggle={() => toggle('upcoming')} controls="long-pto-table" />
            ) : undefined
          }
        >
          <OverviewTable
            id="long-pto-table"
            label="Upcoming PTO of 3 days or more"
            loading={loading}
            hasRows={upcomingRows.length > 0}
            empty={{ title: 'No long PTO coming up', body: 'PTO of 3 days or more that starts in the next 30 days appears here.' }}
            columns={[{ label: 'Employee' }, { label: 'Dates' }, { label: 'Length', align: 'right' }]}
          >
            {upcomingRows.map((entry) => (
              <TableRow key={entry._id}>
                <TableCell sx={{ minWidth: 200 }}>
                  <PersonCell size={24} name={entry.employee.name} secondary={teamOf(entry.employee)} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    {formatDateRange(entry.startDate, entry.endDate)}
                    <PendingPill entry={entry} />
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {formatDays(entryDays(entry.startDate, entry.endDate), 0)}
                </TableCell>
              </TableRow>
            ))}
          </OverviewTable>
        </SectionCard>

        <SectionCard
          id="pto-pacing"
          flush
          title="Ahead of PTO pace"
          description="At least 8h and 20% more PTO used than an even pace through the year"
          footer={
            ptoPacingOver.length > VISIBLE_ROWS ? (
              <ShowAllFooter total={ptoPacingOver.length} noun="employee" expanded={expanded.pacing} onToggle={() => toggle('pacing')} controls="pto-pacing-table" />
            ) : undefined
          }
        >
          <OverviewTable
            id="pto-pacing-table"
            label="Employees ahead of PTO pace"
            loading={loading}
            hasRows={pacingRows.length > 0}
            empty={{ title: 'No one is ahead of pace', body: 'Employees using PTO well ahead of an even pace for the year appear here.' }}
            columns={[{ label: 'Employee' }, { label: 'Used', align: 'right' }, { label: 'Expected', align: 'right' }, { label: 'Over by', align: 'right' }]}
          >
            {pacingRows.map((row) => (
              <TableRow key={row.employee._id}>
                <TableCell sx={{ minWidth: 200 }}>
                  <PersonCell size={24} name={row.employee.name} secondary={teamOf(row.employee)} />
                </TableCell>
                <TableCell align="right">{formatHours(Math.round(row.used))}</TableCell>
                <TableCell align="right">{formatHours(Math.round(row.expectedUsed))}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  +{formatHours(Math.round(row.overBy))}
                </TableCell>
              </TableRow>
            ))}
          </OverviewTable>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default TeamOverviewPanel;
